import React, { useEffect } from "react";
import { useTranslate, useOne, useUpdate } from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Row,
  Col,
  Button,
  Select,
  Switch,
  Spin,
  Tabs,
  Badge,
} from "antd";
import { SaveOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";

type PricingPlanEditModalProps = {
  open: boolean;
  planId: number | string | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const PricingPlanEditModal: React.FC<PricingPlanEditModalProps> = ({
  open,
  planId,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const [form] = Form.useForm();

  const { data, isLoading } = useOne({
    resource: "pricing-plans",
    id: planId ?? "",
    queryOptions: {
      enabled: open && !!planId,
    },
  });

  const record = data?.data;

  const { mutate: updatePlan, isLoading: isUpdating } = useUpdate();

  const { selectProps: productSelectProps } = useSelect({
    resource: "products",
    optionLabel: "name",
    optionValue: "id",
  });

  const watchedProducts = Form.useWatch("products", form);
  const productCount =
    watchedProducts?.filter((p: any) => p?.productId).length ?? 0;

  useEffect(() => {
    if (record && open) {
      const products = (record.pricingPlanProducts ?? []).map((item: any) => ({
        productId: item.product?.id,
        customPrice: item.price,
      }));
      form.setFieldsValue({
        name: record.name,
        description: record.description,
        isActive: record.isActive,
        products,
      });
    }
    if (!open) {
      form.resetFields();
    }
  }, [record, open, form]);

  const validateDuplicateProduct =
    (fieldIndex: number) => (_: any, value: any) => {
      if (!value) return Promise.resolve();
      const products = form.getFieldValue("products") || [];
      const duplicateCount = products.filter(
        (p: any, i: number) => p?.productId === value && i !== fieldIndex,
      ).length;
      if (duplicateCount > 0) {
        return Promise.reject(
          new Error(_t("pricing-plans.errors.duplicateProduct")),
        );
      }
      return Promise.resolve();
    };

  const handleFinish = (values: any) => {
    if (!planId) return;
    updatePlan(
      { resource: "pricing-plans", id: planId, values },
      {
        onSuccess: () => {
          form.resetFields();
          onCancel();
          onSuccess?.();
        },
      },
    );
  };

  return (
    <Modal
      title={_t("pricing-plans.titles.edit")}
      open={open}
      onCancel={onCancel}
      width={800}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{_t("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isUpdating}
            onClick={() => form.submit()}
          >
            {_t("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="!mt-2"
        >
          <Tabs
            defaultActiveKey="info"
            items={[
              {
                key: "info",
                label: _t("pricing-plans.fields.planInfo"),
                children: (
                  <div className="pt-2">
                    <Row gutter={[24, 0]}>
                      <Col xs={24} sm={16}>
                        <Form.Item
                          label={_t("pricing-plans.fields.name")}
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: _t("fields.errors.required"),
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          label={_t("pricing-plans.fields.isActive")}
                          name="isActive"
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={24}>
                        <Form.Item
                          label={_t("pricing-plans.fields.description")}
                          name="description"
                        >
                          <Input.TextArea rows={4} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                ),
              },
              {
                key: "products",
                label: (
                  <Space size={6}>
                    {_t("pricing-plans.fields.productsSection")}
                    {productCount > 0 && (
                      <Badge count={productCount} color="orange" />
                    )}
                  </Space>
                ),
                children: (
                  <div className="pt-2">
                    <Form.List name="products">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.length > 0 && (
                            <div className="flex gap-2 mb-1 px-1">
                              <span className="flex-1 text-xs text-gray-400">
                                {_t("pricing-plans.fields.product")}
                              </span>
                              <span className="w-[160px] text-xs text-gray-400">
                                {_t("pricing-plans.fields.customPrice")}
                              </span>
                              <span className="w-8" />
                            </div>
                          )}
                          {fields.map(({ key, name, ...restField }) => (
                            <div key={key} className="flex gap-2 items-start">
                              <Form.Item
                                {...restField}
                                name={[name, "productId"]}
                                className="flex-1"
                                rules={[
                                  {
                                    required: true,
                                    message: _t("fields.errors.required"),
                                  },
                                  {
                                    validator: validateDuplicateProduct(name),
                                  },
                                ]}
                              >
                                <Select {...productSelectProps} />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, "customPrice"]}
                                className="w-[160px]"
                              >
                                <InputNumber
                                  min={0}
                                  className="w-full"
                                  addonAfter="Cr."
                                />
                              </Form.Item>
                              <Form.Item>
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(name)}
                                />
                              </Form.Item>
                            </div>
                          ))}
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              block
                              icon={<PlusOutlined />}
                            >
                              {_t("pricing-plans.actions.addProduct")}
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </div>
                ),
              },
            ]}
          />
        </Form>
      </Spin>
    </Modal>
  );
};
